import type { FC, ReactNode } from 'react'
import type { Category, PostSummary } from '@/types'

import { useState } from 'react'
import Link from 'next/link'
import { Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { buttonVariants } from '@/components/ui/button'
import { PostCard } from '@/components/journal/post-card'

interface JournalListingProps {
    posts: PostSummary[]
    categories: Category[]
    totalCount: number
    activeCategoryId?: string | null
}

const chipClass = (active: boolean): string => {
    return cn(
        'inline-flex h-10 items-center gap-2 rounded-full border px-4 text-sm whitespace-nowrap transition-colors',
        active
            ? 'border-foreground bg-foreground text-background'
            : 'border-border bg-card/70 text-foreground/80 hover:border-foreground/40 hover:text-foreground'
    )
}

const countClass = (active: boolean): string => {
    return cn('text-xs tabular-nums', active ? 'text-background/75' : 'text-muted-foreground')
}

export const JournalListing: FC<JournalListingProps> = ({
    posts,
    categories,
    totalCount,
    activeCategoryId = null
}): ReactNode => {
    const [search, setSearch] = useState('')

    const term = search.trim().toLowerCase()
    const visible = term
        ? posts.filter(post => [post.title, post.excerpt, post.category_name].some(value => value?.toLowerCase().includes(term)))
        : posts

    return (
        <div>
            <div className='flex flex-col gap-5 border-y border-border py-5 lg:flex-row lg:items-center lg:justify-between'>
                <nav aria-label="Topics" className='no-scrollbar -mx-5 overflow-x-auto px-5 sm:mx-0 sm:px-0'>
                    <ul className='flex w-max gap-2 sm:w-auto sm:flex-wrap'>
                        <li>
                            <Link
                                href='/journal'
                                aria-current={activeCategoryId ? undefined : 'page'}
                                className={chipClass(!activeCategoryId)}
                            >
                                All stories
                                <span className={countClass(!activeCategoryId)}>{totalCount}</span>
                            </Link>
                        </li>

                        {categories.map(category => (
                            <li key={category.id}>
                                <Link
                                    href={`/journal/category/${category.slug}`}
                                    aria-current={category.id === activeCategoryId ? 'page' : undefined}
                                    className={chipClass(category.id === activeCategoryId)}
                                >
                                    <span data-mdlfy-cms={`categories:${category.id}:name`}>{category.name}</span>
                                    <span className={countClass(category.id === activeCategoryId)}>{category.post_count}</span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='relative w-full shrink-0 lg:w-72'>
                    <label htmlFor='journal-search' className='sr-only'>
                        Search stories
                    </label>

                    <Search aria-hidden='true' className='pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 text-muted-foreground' />

                    <Input
                        id='journal-search'
                        type='search'
                        value={search}
                        autoComplete='off'
                        placeholder="Search stories"
                        onChange={event => setSearch(event.target.value)}
                        className='rounded-full pl-10'
                    />
                </div>
            </div>

            <p role='status' className='mt-6 min-h-5 text-sm text-muted-foreground'>
                {term ? `${visible.length} ${visible.length === 1 ? "story" : "stories"} matching “${search.trim()}”` : ''}
            </p>

            {visible.length > 0 && (
                <div className='mt-6 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3'>
                    {visible.map((post, index) => (
                        <PostCard
                            key={post.id}
                            post={post}
                            priority={index < 3}
                            headingLevel='h2'
                        />
                    ))}
                </div>
            )}

            {visible.length === 0 && term && (
                <div className='mt-6 flex flex-col items-center rounded-2xl border border-dashed border-border px-6 py-16 text-center'>
                    <p className='font-heading text-2xl'>
                        Nothing matches that search yet
                    </p>

                    <p className='mt-3 max-w-md text-muted-foreground'>
                        Try a different word, like a room, a material or a color.
                    </p>

                    <button
                        type='button'
                        onClick={() => setSearch('')}
                        className={buttonVariants({ variant: 'outline', className: 'mt-6' })}
                    >
                        <X />
                        Clear search
                    </button>
                </div>
            )}

            {visible.length === 0 && !term && (
                <div className='mt-6 flex flex-col items-center rounded-2xl border border-dashed border-border px-6 py-16 text-center'>
                    <p className='font-heading text-2xl'>
                        New stories are on the way
                    </p>

                    <p className='mt-3 max-w-md text-muted-foreground'>
                        There is nothing published here just yet.
                        {activeCategoryId ? " In the meantime, the rest of the journal is waiting for you." : " Check back soon."}
                    </p>

                    {activeCategoryId && (
                        <Link href='/journal' className={buttonVariants({ variant: 'outline', className: 'mt-6' })}>
                            Browse all stories
                        </Link>
                    )}
                </div>
            )}
        </div>
    )
}
